import { colors } from '@packages/core/styles';
import { Icon, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ssm5TextStyles } from '../ssm5.styles';

const OPEN_ICON_SIZE = 16;

/** Props for {@link Ssm5ReprogrammingNotice}. */
export type Ssm5ReprogrammingNoticeProps = {
    /** Where SSM5 opens to reprogram the module; without it the notice names the link with nothing to open. */
    reproUrl?: string;
};

const useStyles = createUseStyles({
    // The design's informative banner: the brand's lighter fill, a 16px inset, the link 8px after the text.
    banner: {
        alignItems: 'center',
        backgroundColor: colors.blueFour,
        display: 'flex',
        gap: 8,
        padding: 16,
    },
    // The link keeps its color under the pointer; the icon follows the text as the design draws it.
    link: {
        '&:hover': {
            color: colors.blueLink,
            textDecoration: 'underline',
        },
        alignItems: 'center',
        color: colors.blueLink,
        display: 'inline-flex',
        gap: 8,
        textDecoration: 'none',
    },
    text: {
        flex: 1,
        minWidth: 0,
    },
});

/**
 * The notice that a module can be reprogrammed, with the link that opens SSM5, where reprogramming runs.
 *
 * @param props - {@link Ssm5ReprogrammingNoticeProps}
 * @returns the notice
 */
export function Ssm5ReprogrammingNotice({ reproUrl }: Ssm5ReprogrammingNoticeProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const link = (
        <>
            <Typography color="blueLink" style={ssm5TextStyles.noticeText} variant="default">
                {t('ssm5:module.reprogramming.open')}
            </Typography>
            <Icon color="blueLink" name="open-in-new" size={OPEN_ICON_SIZE} />
        </>
    );

    return (
        <div className={classes.banner}>
            <div className={classes.text}>
                <Typography style={ssm5TextStyles.noticeText} variant="default">
                    {t('ssm5:module.reprogramming.available')}
                </Typography>
            </div>
            {reproUrl === undefined ? (
                <span className={classes.link}>{link}</span>
            ) : (
                <a className={classes.link} href={reproUrl} rel="noreferrer" target="_blank">
                    {link}
                </a>
            )}
        </div>
    );
}
